import type { PasswordRule } from '@didto/domain';
import { PASSWORD_RULES, checkNewPassword, passwordProblems } from '@didto/domain';
import { Button, Field, Input, Loading, Notice } from '@didto/ui';
import type { FormEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@/app/session';
import { errorText, isNetworkError } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import type { RecoveryStep } from '@/lib/passwordRecovery';
import {
  firstRecoveryStep,
  isRecoverySession,
  passwordRefusal,
  rulesToName,
  settleRecoveryStep,
} from '@/lib/passwordRecovery';
import { supabase } from '@/lib/supabase';
import { AuthFrame } from './AuthFrame';
import './auth.css';

/** How long "Password changed" stays on screen before the sign-in page (signing out comes first). */
const SUCCESS_PAUSE_MS = 2_000;

type Step = 'checking' | RecoveryStep | 'done';

const RULE_LABEL = new Map(PASSWORD_RULES.map((r) => [r.rule, r.label]));
const RULES_ID = 'reset-password-rules';

/**
 * ADR-041: a new password for the login a password reset link signed in. A login with an
 * authenticator gives its code first (Auth refuses the change otherwise, and the authenticator stays
 * enrolled). Once the password is changed the login is signed out and goes back to sign in.
 */
export function ResetPasswordPage() {
  const s = useSession();
  const nav = useNavigate();
  // ADR-037: Auth checks everything here; the connection banner above says why it is paused
  const online = useOnline();
  const [step, setStep] = useState<Step>('checking');
  const [checkError, setCheckError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [signedIn, setSignedIn] = useState(false);
  const [email, setEmail] = useState('');
  const [ended, setEnded] = useState(false);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [refused, setRefused] = useState<PasswordRule[] | null>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<number | null>(null);
  // as the page opened: forgetting the link later (endRecovery) must not re-decide the step
  const [linkFailed] = useState(() => s.linkError !== null);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const loadFactor = useCallback(async () => {
    const { data, error: err } = await supabase.auth.mfa.listFactors();
    if (err) throw err;
    setFactorId(data?.totp.find((f) => f.status === 'verified')?.id ?? null);
  }, []);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        // both wait until the client has read the address: a reset link's session, or its error
        const init = await supabase.auth.initialize();
        const { data } = await supabase.auth.getSession();
        const session = data.session;
        const recovery = isRecoverySession(session?.access_token);
        let aal: { currentLevel: string | null; nextLevel: string | null } | null = null;
        if (recovery) {
          const r = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
          if (r.error) throw r.error;
          aal = r.data;
        }
        const next = firstRecoveryStep({
          recovery,
          // the link's tokens were refused (a failed link's error is in s.linkError already)
          linkFailed: linkFailed || (!!init.error && !isNetworkError(init.error)),
          aal,
        });
        if (next === 'verify') await loadFactor();
        if (!alive) return;
        setSignedIn(!!session);
        setEmail(session?.user.email ?? '');
        setCheckError(null);
        setStep((cur) => settleRecoveryStep(cur, next));
      } catch (e) {
        if (alive) setCheckError(errorText(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, [linkFailed, attempt, loadFactor, s.recovery]);

  /**
   * Out of the reset without changing anything: the link's session is signed out. supabase-js
   * removes it from this browser even when Auth cannot be reached.
   */
  const cancel = async () => {
    setBusy(true);
    await s.signOut();
    s.endRecovery();
    nav('/login', { replace: true });
  };

  /** Away from a link that failed, or from the page opened without one. */
  const leave = () => {
    s.endRecovery();
    nav(signedIn ? '/' : '/login', { replace: true });
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    if (!factorId) return;
    setBusy(true);
    setError(null);
    try {
      const { error: err } = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        code: code.trim(),
      });
      if (err) {
        // a dropped connection is not a wrong code
        setError(
          isNetworkError(err)
            ? errorText(err)
            : 'That code was not accepted. Codes rotate every 30 seconds.',
        );
        return;
      }
      setCode('');
      setStep('form');
    } catch (thrown) {
      setError(errorText(thrown));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Signed out, then the sign-in page once "Password changed" has been on screen for a moment. Auth
   * has already ended the login's other sessions; supabase-js removes this one from the browser even
   * when Auth cannot be reached.
   */
  const finish = async () => {
    const started = Date.now();
    await s.signOut();
    s.endRecovery();
    timer.current = window.setTimeout(
      () => nav('/login', { replace: true, state: { passwordChanged: true } }),
      Math.max(0, SUCCESS_PAUSE_MS - (Date.now() - started)),
    );
  };

  const check = checkNewPassword(password, confirm);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!check.ok) return;
    setBusy(true);
    setError(null);
    setRefused(null);
    try {
      const { error: err } = await supabase.auth.updateUser({ password });
      if (err) {
        const why = passwordRefusal(err);
        if (why.kind === 'weak') {
          const rules = why.pwned ? null : rulesToName(passwordProblems(password), why.tooLong);
          setRefused(rules);
          setError(
            why.pwned
              ? 'This password appears in a known data breach. Choose a different one.'
              : rules
                ? null
                : errorText(err),
          );
        } else if (why.kind === 'same') {
          setError('That is the password this login already has. Choose a new one.');
        } else if (why.kind === 'needs-code') {
          await loadFactor();
          setError('Enter your authenticator code first.');
          setStep('verify');
        } else if (why.kind === 'ended') {
          // Auth no longer knows the session: it leaves this browser too, and the way on is to sign in
          await s.signOut();
          setSignedIn(false);
          setEnded(true);
          setStep('link-error');
        } else {
          setError(errorText(err));
        }
        return;
      }
      setPassword('');
      setConfirm('');
      setStep('done');
      await finish();
    } catch (thrown) {
      setError(errorText(thrown));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame
      title="Reset your password"
      subtitle={email && step !== 'done' ? `For ${email}` : 'Owner and manager logins'}
    >
      {step === 'checking' &&
        (checkError ? (
          <div className="d-stack">
            <Notice tone="danger" role="alert" testId="reset-check-error">
              {checkError}
            </Notice>
            <Button variant="primary" block onClick={() => setAttempt((n) => n + 1)}>
              Try again
            </Button>
          </div>
        ) : (
          <Loading label="Checking your reset link…" />
        ))}

      {step === 'link-error' && (
        <div className="d-stack" data-testid="reset-link-error">
          <Notice
            tone="danger"
            role="alert"
            title={
              ended
                ? 'This reset link’s session has ended'
                : 'This password reset link is invalid or has expired'
            }
          >
            A reset link works once and only for a limited time. Ask for a new password reset email
            and open the link in the newest one.
          </Notice>
          <p className="d-help">
            The owner sends one from the Supabase dashboard: Authentication, Users, the login, Send
            password recovery.
          </p>
          <Button variant="primary" block onClick={leave} data-testid="reset-leave">
            {signedIn ? 'Back to the app' : 'Back to sign in'}
          </Button>
        </div>
      )}

      {step === 'no-link' && (
        <div className="d-stack" data-testid="reset-no-link">
          <Notice tone="info" title="Open the link in your password reset email">
            A new password is chosen from that link. This page does nothing on its own.
          </Notice>
          <Button variant="primary" block onClick={leave} data-testid="reset-leave">
            {signedIn ? 'Back to the app' : 'Back to sign in'}
          </Button>
        </div>
      )}

      {step === 'verify' && (
        <form onSubmit={(e) => void verify(e)} className="d-stack" data-testid="reset-verify">
          <p className="d-muted">
            Two-step verification is on for this login: enter the code from your authenticator app
            before choosing a new password. Your authenticator stays as it is.
          </p>
          <Field label="6-digit code" help="From your authenticator app">
            {(id) => (
              <Input
                id={id}
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="signin__code"
                autoFocus
                data-testid="reset-mfa-code"
              />
            )}
          </Field>
          {!factorId && (
            <Notice tone="danger" role="alert">
              This login’s authenticator could not be found. Reload the page; if it keeps happening,
              tell the owner.
            </Notice>
          )}
          {error && (
            <Notice tone="danger" role="alert" testId="reset-error">
              {error}
            </Notice>
          )}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            block
            busy={busy}
            disabled={!online || !factorId || code.trim().length < 6}
            data-testid="reset-mfa-submit"
          >
            Continue
          </Button>
          <Button variant="quiet" onClick={() => void cancel()} disabled={busy}>
            Cancel and sign out
          </Button>
        </form>
      )}

      {step === 'form' && (
        <form
          onSubmit={(e) => void save(e)}
          className="d-stack"
          noValidate
          data-testid="reset-form"
        >
          {/* for password managers: the login this new password belongs to */}
          <input
            type="email"
            name="username"
            autoComplete="username"
            value={email}
            readOnly
            tabIndex={-1}
            aria-hidden
            className="d-sr-only"
          />
          <Field label="New password">
            {(id) => (
              <Input
                id={id}
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setRefused(null);
                }}
                aria-describedby={RULES_ID}
                autoFocus
                data-testid="reset-password"
              />
            )}
          </Field>
          <PasswordRules id={RULES_ID} password={password} />
          <Field
            label="Confirm password"
            error={
              confirm.length > 0 && !check.matches ? 'The two passwords are not the same.' : null
            }
          >
            {(id) => (
              <Input
                id={id}
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-confirm"
              />
            )}
          </Field>
          {(error || refused) && (
            <Notice
              tone="danger"
              role="alert"
              testId="reset-error"
              title={refused ? 'This password is not accepted' : undefined}
            >
              {refused ? (
                <ul className="pwrules__refused">
                  {refused.map((r) => (
                    <li key={r}>{RULE_LABEL.get(r)}</li>
                  ))}
                </ul>
              ) : (
                error
              )}
            </Notice>
          )}
          <p className="d-help">
            Other devices signed in with this login can no longer renew their sign-in once the
            password changes: they are signed out within the hour.
          </p>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            block
            busy={busy}
            disabled={!online || !check.ok}
            data-testid="reset-submit"
          >
            Save new password
          </Button>
          <Button variant="quiet" onClick={() => void cancel()} disabled={busy}>
            Cancel and sign out
          </Button>
        </form>
      )}

      {step === 'done' && (
        <div className="d-stack" data-testid="reset-done">
          <Notice tone="success" role="status" title="Password changed">
            Signing you out. Sign in again with the new password.
          </Notice>
        </div>
      )}
    </AuthFrame>
  );
}

/** The policy, ticked off as the password is typed: a mark and words, never colour alone. */
function PasswordRules({ id, password }: { id: string; password: string }) {
  const broken = new Set(passwordProblems(password));
  return (
    <ul id={id} className="pwrules" data-testid="password-rules">
      {PASSWORD_RULES.filter((r) => r.rule !== 'max' || broken.has('max')).map((r) => {
        const met = !broken.has(r.rule);
        return (
          <li
            key={r.rule}
            className={met ? 'pwrules__rule pwrules__rule--met' : 'pwrules__rule'}
            data-rule={r.rule}
            data-met={met}
          >
            <span className="pwrules__mark" aria-hidden>
              {met ? '✓' : '○'}
            </span>
            {r.label}
            <span className="d-sr-only">{met ? ' (done)' : ' (still needed)'}</span>
          </li>
        );
      })}
    </ul>
  );
}
